import { useState, useMemo } from "react";
import "./App.css";
import logo from "./logo.svg";

const TIERS = [
  {
    id: 1,
    name: "Tier 1",
    label: "Conservative",
    bonusPercent: 10,
    maxCS: 200000,
    pcr: 0,
    description: "No profit conversion, maximum credit support cap",
  },
  {
    id: 2,
    name: "Tier 2",
    label: "Balanced",
    bonusPercent: 35,
    maxCS: 100000,
    pcr: 15,
    description: "Low PCR with high credit support cap",
  },
  {
    id: 3,
    name: "Tier 3",
    label: "Growth",
    bonusPercent: 50,
    maxCS: 20000,
    pcr: 20,
    description: "Balanced bonus and profit conversion",
  },
  {
    id: 4,
    name: "Tier 4",
    label: "Aggressive",
    bonusPercent: 100,
    maxCS: 10000,
    pcr: 30,
    description: "Maximum bonus with higher profit conversion",
  },
];

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function calculateVolumeReward(lots) {
  if (lots <= 0) return 0;

  let reward = 0;

  // First 300 lots at $1
  const tier1Lots = Math.min(lots, 300);
  reward += tier1Lots * 1;

  // 301-1000 lots at $2
  if (lots > 300) {
    const tier2Lots = Math.min(lots - 300, 700);
    reward += tier2Lots * 2;
  }

  // Above 1000 lots at $4
  if (lots > 1000) {
    const tier3Lots = lots - 1000;
    reward += tier3Lots * 4;
  }

  return reward;
}

function App() {
  const [depositAmount, setDepositAmount] = useState("");
  const [selectedTier, setSelectedTier] = useState(1);
  const [isDirectUser, setIsDirectUser] = useState(true);
  const [dailyVolume, setDailyVolume] = useState("");
  const [profitWithdrawal, setProfitWithdrawal] = useState("");
  const [balanceDropped, setBalanceDropped] = useState(false);

  const tier = TIERS.find((t) => t.id === selectedTier);
  const deposit = parseFloat(depositAmount) || 0;
  const volume = parseFloat(dailyVolume) || 0;
  const profit = parseFloat(profitWithdrawal) || 0;

  const calculations = useMemo(() => {
    const rawBonus = deposit * (tier.bonusPercent / 100);
    const creditSupport = Math.min(rawBonus, tier.maxCS);
    const totalTradingEquity = deposit + creditSupport;

    // PCR calculations for profit withdrawal
    const earnedCS = profit * (tier.pcr / 100);
    const cashPayout = profit * (1 - tier.pcr / 100);
    const cryptoFee = cashPayout * 0.006;
    const netCryptoPayout = cashPayout - cryptoFee;

    // Volume reward (only for direct users)
    const volumeReward = isDirectUser ? calculateVolumeReward(volume) : 0;

    return {
      creditSupport,
      rawBonus,
      totalTradingEquity,
      earnedCS,
      cashPayout,
      cryptoFee,
      netCryptoPayout,
      volumeReward,
      isCapped: rawBonus > tier.maxCS,
    };
  }, [deposit, tier, profit, volume, isDirectUser]);

  return (
    <div className="app">
      <div className="background-pattern"></div>

      <header className="header">
        <div className="logo">
          <img src={logo} alt="Bank of Solidus" className="logo-image" />
          <span className="logo-divider"></span>
          <span className="logo-subtitle">Bonus Calculator</span>
        </div>
      </header>

      <main className="main">
        <div className="calculator-grid">
          {/* Left Panel - Inputs */}
          <section className="panel input-panel">
            <h2>
              <span className="section-icon">⚙</span>
              Configuration
            </h2>

            {/* User Type Toggle */}
            <div className="input-group">
              <label>Account Type</label>
              <div className="toggle-container">
                <button
                  className={`toggle-btn ${isDirectUser ? "active" : ""}`}
                  onClick={() => setIsDirectUser(true)}>
                  <span className="toggle-icon">👤</span>
                  Direct User
                </button>
                <button
                  className={`toggle-btn ${!isDirectUser ? "active" : ""}`}
                  onClick={() => setIsDirectUser(false)}>
                  <span className="toggle-icon">🤝</span>
                  IB Referred
                </button>
              </div>
              {!isDirectUser && (
                <p className="info-text warning">
                  ⚠ Volume rebates are not available for IB referred users
                </p>
              )}
            </div>

            {/* Tier Selection */}
            <div className="input-group">
              <label>Select Bonus Tier</label>
              <div className="tier-grid">
                {TIERS.map((t) => (
                  <button
                    key={t.id}
                    className={`tier-card ${
                      selectedTier === t.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedTier(t.id)}>
                    <div className="tier-header">
                      <span className="tier-name">{t.name}</span>
                      <span className="tier-bonus">{t.bonusPercent}%</span>
                    </div>
                    <div className="tier-details">
                      <span className="tier-label">{t.label}</span>
                      <span className="tier-pcr">PCR: {t.pcr}%</span>
                      <span className="tier-cap">
                        Cap: {formatCurrency(t.maxCS)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Deposit Amount */}
            <div className="input-group">
              <label htmlFor="deposit">Net Deposit (ND)</label>
              <div className="input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  id="deposit"
                  type="number"
                  placeholder="Enter deposit amount"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                />
              </div>
            </div>

            {/* Profit Withdrawal */}
            <div className="input-group">
              <label htmlFor="profit">Profit Withdrawal Amount</label>
              <div className="input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  id="profit"
                  type="number"
                  placeholder="Enter profit to withdraw"
                  value={profitWithdrawal}
                  onChange={(e) => setProfitWithdrawal(e.target.value)}
                />
              </div>
              <p className="info-text">
                PCR ({tier.pcr}%) will be applied to profit withdrawals
              </p>
            </div>

            {/* Balance Drop Toggle - Recovery Scenario */}
            <div className="input-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={balanceDropped}
                  onChange={(e) => setBalanceDropped(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <span>Lost deposit & recovered with Credit Support</span>
              </label>
              <p className="info-text" style={{ marginTop: "8px" }}>
                Enable if client lost their deposit and made profit back using
                only the Credit Support (company money)
              </p>

              {balanceDropped && (
                <div className="recovery-scenario-box">
                  <div className="scenario-step">
                    <span className="step-number">1</span>
                    <span>
                      Client Funds exhausted (losses consumed deposit)
                    </span>
                  </div>
                  <div className="scenario-step">
                    <span className="step-number">2</span>
                    <span>Trading continues with Credit Support only</span>
                  </div>
                  <div className="scenario-step">
                    <span className="step-number">3</span>
                    <span>Account value grows from CS trading</span>
                  </div>
                  <div className="scenario-note">
                    <span>
                      💡 The "Profit Withdrawal Amount" above will be used as
                      the profit made from CS trading
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Right Panel - Results */}
          <section className="panel results-panel">
            <h2>
              <span className="section-icon">📈</span>
              Calculation Results
            </h2>

            {/* Credit Support Card */}
            <div className="result-card primary">
              <div className="result-card-header">
                <h3>Credit Support (CS)</h3>
                <span className="badge">
                  {tier.name} - {tier.bonusPercent}% Bonus
                </span>
              </div>
              <div className="result-card-body">
                <div className="result-main">
                  <span className="result-value">
                    {formatCurrency(calculations.creditSupport)}
                  </span>
                  {calculations.isCapped && (
                    <span className="cap-notice">
                      Capped at {formatCurrency(tier.maxCS)}
                    </span>
                  )}
                </div>
                <div className="result-breakdown">
                  <div className="breakdown-item">
                    <span>Net Deposit (ND)</span>
                    <span>{formatCurrency(deposit)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span>Bonus Rate</span>
                    <span>{tier.bonusPercent}%</span>
                  </div>
                  <div className="breakdown-item">
                    <span>Raw Bonus</span>
                    <span>{formatCurrency(calculations.rawBonus)}</span>
                  </div>
                  <div className="breakdown-item highlight">
                    <span>Total Trading Equity</span>
                    <span>
                      {formatCurrency(calculations.totalTradingEquity)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* PCR Card */}
            {profit > 0 && (
              <div className="result-card secondary">
                <div className="result-card-header">
                  <h3>Profit Conversion Rate (PCR)</h3>
                  <span className="badge">{tier.pcr}% PCR</span>
                </div>
                <div className="result-card-body">
                  <div className="pcr-flow">
                    <div className="pcr-item">
                      <span className="pcr-label">Profit Withdrawal</span>
                      <span className="pcr-value">
                        {formatCurrency(profit)}
                      </span>
                    </div>
                    <div className="pcr-arrow">→</div>
                    <div className="pcr-split">
                      <div className="pcr-item earned">
                        <span className="pcr-label">
                          Earned CS ({tier.pcr}%)
                        </span>
                        <span className="pcr-value">
                          {formatCurrency(calculations.earnedCS)}
                        </span>
                      </div>
                      <div className="pcr-item cash">
                        <span className="pcr-label">
                          Cash Payout ({100 - tier.pcr}%)
                        </span>
                        <span className="pcr-value">
                          {formatCurrency(calculations.cashPayout)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="withdrawal-details">
                    <h4>Withdrawal Breakdown</h4>
                    <div className="breakdown-item">
                      <span>Cash before fees</span>
                      <span>{formatCurrency(calculations.cashPayout)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Crypto fee (0.6%)</span>
                      <span>-{formatCurrency(calculations.cryptoFee)}</span>
                    </div>
                    <div className="breakdown-item highlight">
                      <span>Net to wallet (Crypto)</span>
                      <span>
                        {formatCurrency(calculations.netCryptoPayout)}
                      </span>
                    </div>
                    <div className="breakdown-item">
                      <span>Net via Bank Wire</span>
                      <span>{formatCurrency(calculations.cashPayout)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recovery Scenario - Lost deposit, recovered with CS */}
            {balanceDropped && deposit > 0 && (
              <div className="result-card recovery">
                <div className="result-card-header">
                  <h3>Recovery Scenario Analysis</h3>
                  <span className="badge recovery-badge">
                    Trading on CS Only
                  </span>
                </div>
                <div className="result-card-body">
                  <div className="recovery-timeline">
                    <div className="timeline-item">
                      <div className="timeline-icon start">📥</div>
                      <div className="timeline-content">
                        <span className="timeline-label">
                          Initial Deposit (ND)
                        </span>
                        <span className="timeline-value">
                          {formatCurrency(deposit)}
                        </span>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon bonus">🎁</div>
                      <div className="timeline-content">
                        <span className="timeline-label">
                          Credit Support Granted
                        </span>
                        <span className="timeline-value accent">
                          {formatCurrency(calculations.creditSupport)}
                        </span>
                      </div>
                    </div>
                    <div className="timeline-item">
                      <div className="timeline-icon loss">📉</div>
                      <div className="timeline-content">
                        <span className="timeline-label">
                          After Loss (Client Funds Exhausted)
                        </span>
                        <span className="timeline-value loss">
                          Client Funds: $0 | CS:{" "}
                          {formatCurrency(calculations.creditSupport)}
                        </span>
                      </div>
                    </div>
                    {profit > 0 && (
                      <>
                        <div className="timeline-item">
                          <div className="timeline-icon recovery">📈</div>
                          <div className="timeline-content">
                            <span className="timeline-label">
                              Current Account Value (After Recovery)
                            </span>
                            <span className="timeline-value success">
                              {formatCurrency(deposit + profit)}
                            </span>
                          </div>
                        </div>
                        <div className="timeline-item">
                          <div className="timeline-icon profit">💰</div>
                          <div className="timeline-content">
                            <span className="timeline-label">
                              Profit to Withdraw
                            </span>
                            <span className="timeline-value highlight">
                              {formatCurrency(profit)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {profit > 0 && (
                    <div className="recovery-withdrawal">
                      <h4>Profit Withdrawal Calculation</h4>
                      <div className="pcr-flow">
                        <div className="pcr-item">
                          <span className="pcr-label">Profit to Withdraw</span>
                          <span className="pcr-value">
                            {formatCurrency(profit)}
                          </span>
                        </div>
                        <div className="pcr-arrow">→</div>
                        <div className="pcr-split">
                          <div className="pcr-item earned">
                            <span className="pcr-label">
                              Earned CS ({tier.pcr}%)
                            </span>
                            <span className="pcr-value">
                              {formatCurrency(calculations.earnedCS)}
                            </span>
                          </div>
                          <div className="pcr-item cash">
                            <span className="pcr-label">
                              Cash Payout ({100 - tier.pcr}%)
                            </span>
                            <span className="pcr-value">
                              {formatCurrency(calculations.cashPayout)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="result-breakdown">
                        <div className="breakdown-item">
                          <span>Cash before fees</span>
                          <span>{formatCurrency(calculations.cashPayout)}</span>
                        </div>
                        <div className="breakdown-item">
                          <span>Crypto fee (0.6%)</span>
                          <span>-{formatCurrency(calculations.cryptoFee)}</span>
                        </div>
                        <div className="breakdown-item highlight">
                          <span>Net to wallet (Crypto)</span>
                          <span>
                            {formatCurrency(calculations.netCryptoPayout)}
                          </span>
                        </div>
                        <div className="breakdown-item">
                          <span>Net via Bank Wire (no fee)</span>
                          <span>{formatCurrency(calculations.cashPayout)}</span>
                        </div>
                      </div>

                      <div className="after-withdrawal-summary">
                        <h4>After Withdrawal</h4>
                        <div className="summary-row">
                          <span>Net Deposit (ND) remains</span>
                          <span>{formatCurrency(deposit)}</span>
                        </div>
                        <div className="summary-row">
                          <span>Total Credit Support</span>
                          <span className="accent">
                            {formatCurrency(
                              calculations.creditSupport + calculations.earnedCS
                            )}
                          </span>
                        </div>
                        <p className="info-text" style={{ marginTop: "12px" }}>
                          Original CS (
                          {formatCurrency(calculations.creditSupport)}) + Earned
                          CS ({formatCurrency(calculations.earnedCS)})
                        </p>
                      </div>
                    </div>
                  )}

                  {!profit && (
                    <p className="info-text" style={{ marginTop: "16px" }}>
                      💡 Enter the profit withdrawal amount above to calculate
                      the recovery scenario.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Summary Card */}
            {deposit > 0 && (
              <div className="result-card summary">
                <div className="result-card-header">
                  <h3>Summary</h3>
                </div>
                <div className="result-card-body">
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="summary-label">Your Deposit</span>
                      <span className="summary-value">
                        {formatCurrency(deposit)}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Credit Support</span>
                      <span className="summary-value accent">
                        {formatCurrency(calculations.creditSupport)}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Trading Power</span>
                      <span className="summary-value highlight">
                        {formatCurrency(calculations.totalTradingEquity)}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Effective Bonus</span>
                      <span className="summary-value">
                        {deposit > 0
                          ? (
                              (calculations.creditSupport / deposit) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Volume Rebate Section - Separate Panel */}
          {isDirectUser && (
            <section className="panel volume-panel">
              <h2>
                <span className="section-icon">📊</span>
                Volume Rebate
              </h2>

              <div className="input-group">
                <label htmlFor="volume">Daily Trading Volume (Lots)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📊</span>
                  <input
                    id="volume"
                    type="number"
                    placeholder="Enter daily lots traded"
                    value={dailyVolume}
                    onChange={(e) => setDailyVolume(e.target.value)}
                  />
                </div>
              </div>

              <div className="volume-tiers-table">
                <div className="volume-tier-row header">
                  <span>Volume Range</span>
                  <span>Rate</span>
                  <span>Your Reward</span>
                </div>
                <div
                  className={`volume-tier-row ${volume > 0 ? "active" : ""}`}>
                  <span>1 - 300 lots</span>
                  <span>$1 / lot</span>
                  <span>{formatCurrency(Math.min(volume, 300) * 1)}</span>
                </div>
                <div
                  className={`volume-tier-row ${volume > 300 ? "active" : ""}`}>
                  <span>301 - 1,000 lots</span>
                  <span>$2 / lot</span>
                  <span>
                    {formatCurrency(
                      volume > 300 ? Math.min(volume - 300, 700) * 2 : 0
                    )}
                  </span>
                </div>
                <div
                  className={`volume-tier-row ${
                    volume > 1000 ? "active" : ""
                  }`}>
                  <span>Above 1,000 lots</span>
                  <span>$4 / lot</span>
                  <span>
                    {formatCurrency(volume > 1000 ? (volume - 1000) * 4 : 0)}
                  </span>
                </div>
                <div className="volume-tier-row total">
                  <span>Total Daily Reward</span>
                  <span></span>
                  <span className="reward-total">
                    {formatCurrency(calculations.volumeReward)}
                  </span>
                </div>
              </div>

              <p className="info-text" style={{ marginTop: "16px" }}>
                💡 Volume rewards are treated as profit and subject to PCR when
                withdrawn
              </p>
            </section>
          )}
        </div>

        {/* Info Section */}
        <section className="info-section">
          <div className="info-card">
            <h3>📋 Tier Comparison</h3>
            <table className="tier-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Bonus %</th>
                  <th>Max Credit Support</th>
                  <th>PCR</th>
                  <th>Profile</th>
                </tr>
              </thead>
              <tbody>
                {TIERS.map((t) => (
                  <tr
                    key={t.id}
                    className={selectedTier === t.id ? "active" : ""}>
                    <td>
                      <strong>{t.name}</strong>
                    </td>
                    <td>{t.bonusPercent}%</td>
                    <td>{formatCurrency(t.maxCS)}</td>
                    <td>{t.pcr}%</td>
                    <td>{t.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="info-card">
            <h3>💡 Key Information</h3>
            <ul className="info-list">
              <li>
                <strong>Credit Support (CS)</strong> is company-issued trading
                credit, not withdrawable but usable for margin.
              </li>
              <li>
                <strong>PCR</strong> converts a portion of profit withdrawals
                into Earned Credit Support.
              </li>
              <li>
                <strong>Volume Rewards</strong> are available only for direct
                users (not IB referred).
              </li>
              <li>
                <strong>Losses</strong> reduce Client Funds first, then Credit
                Support.
              </li>
              <li>
                <strong>Recovery Scenario:</strong> If client loses deposit but
                recovers using CS, profit = Account Value - Original ND. PCR
                still applies.
              </li>
              <li>
                <strong>Crypto withdrawals</strong> incur a 0.6% processing fee;
                bank wires have no company fee.
              </li>
            </ul>
          </div>
        </section>
      </main>

      <footer className="footer">
        <p>
          Bank of Solidus Retail Credit Support & Volume Reward Program
          Calculator
        </p>
        <p className="disclaimer">
          This calculator is for estimation purposes only. Actual values may
          vary.
        </p>
      </footer>
    </div>
  );
}

export default App;
